import {
  Box,
  Button,
  Fade,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { HtmlTooltip } from '../../../../../components/Tooltip/HtmlTooltip'
import { ProductTicketSettingsProps } from '../../../ProductTypes'
import SwitchRound from '../../../../../components/Switch/SwitchRound'
import { useAuthContext } from '../../../../../core/hooks/useAuthContext'
import BasicDateTimePicker from '../../../../../components/DateTime/BasicDateTimePicker'
import { Controller } from 'react-hook-form'

const ProductTicketSettings = ({
  control,
  errors,
  buttonSave,
  watch,
  setValue,
}: ProductTicketSettingsProps) => {
  const { isUserSysAdmin } = useAuthContext()
  const { register } = control

  const isAdmin = isUserSysAdmin()

  const ticketIsNominal = watch('ticketIsNominal')
  const ticketFillDeadlineDays = watch('ticketFillDeadlineDays')
  const ticketAllowTransfer = watch('ticketAllowTransfer')

  return (
    <>
      <Box width="100%" sx={{ p: 3 }}>
        <Typography
          color="#343948"
          fontWeight={700}
          fontSize="16px"
          lineHeight="20px"
          gutterBottom
        >
          Configurações do Ingresso
        </Typography>

        <Stack direction="column" spacing={3} sx={{ pt: 3 }}>
          <Stack direction="column" spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  color: '#38393B',
                }}
              >
                Tipo de ingresso
              </Typography>

              <HtmlTooltip
                title={
                  <Box>
                    <Typography fontWeight={600}>
                      Ingresso Não Nominal
                    </Typography>
                    <Typography variant="body2">
                      O cliente/produtor pode adquirir ingressos sem precisar
                      informar o nome do participante no momento da compra.
                    </Typography>
                    <br />
                    <Typography fontWeight={600}>
                      Ingresso Nominal (Checkout Imediato)
                    </Typography>
                    <Typography variant="body2">
                      No fluxo de checkout, o cliente já informa nome, CPF e
                      e-mail (ou outros campos obrigatórios) de cada
                      participante. Os dados ficam vinculados ao ingresso,
                      bloqueando a transferência, salvo se o produtor habilitar.
                    </Typography>
                  </Box>
                }
              >
                <img
                  src="/assets/icons/infos/infoBlack.svg"
                  alt="info-ticket-type"
                />
              </HtmlTooltip>
            </Stack>

            {/* Radio: Não nominal / Nominal */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <RadioGroup
                row
                sx={{ m: 0 }}
                value={ticketIsNominal ? 'nominal' : 'non_nominal'}
                onChange={(e) => {
                  const isNominal = e.target.value === 'nominal'
                  setValue('ticketIsNominal', isNominal, {
                    shouldDirty: true,
                  })
                  // reseta o que não se aplica
                  if (isNominal) {
                    setValue('ticketFillDeadlineDays', 0, {
                      shouldDirty: true,
                    })
                  } else {
                    setValue('ticketAllowTransfer', false, {
                      shouldDirty: true,
                    })
                  }
                }}
              >
                <FormControlLabel
                  value="non_nominal"
                  control={<Radio disabled={isAdmin} />}
                  label="Não nominal"
                />
                <FormControlLabel
                  value="nominal"
                  control={<Radio disabled={isAdmin} />}
                  label="Nominal (checkout imediato)"
                />
              </RadioGroup>
              {errors.ticketIsNominal?.type === 'required' && (
                <Typography variant="overline" color={'red'}>
                  Campo obrigatório
                </Typography>
              )}
            </Box>

            {/* NÃO nominal → Prazo em dias */}
            <Fade in={!ticketIsNominal} mountOnEnter unmountOnExit>
              <Box>
                <TextField
                  fullWidth
                  disabled={isAdmin}
                  size="small"
                  type="number"
                  label="Prazo para preencher os dados (dias antes do evento)"
                  {...register('ticketFillDeadlineDays')}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, max: 365, inputMode: 'numeric' }}
                  value={ticketFillDeadlineDays ?? 0}
                  onChange={(e) => {
                    const v = e.target.value
                    const num =
                      v === '' ? 0 : Math.max(0, Math.min(365, Number(v)))
                    setValue('ticketFillDeadlineDays', num, {
                      shouldDirty: true,
                    })
                  }}
                  placeholder="Ex.: 7"
                  error={
                    errors.ticketFillDeadlineDays?.type === 'required' ||
                    errors.ticketFillDeadlineDays?.type === 'invalidNumber'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography sx={{ color: '#707275' }}>dias</Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: errors.ticketFillDeadlineDays ? 'red' : '#707275',
                  }}
                >
                  Ex.: 7 = até 7 dias antes do início do evento para informar os
                  dados do participante.
                </Typography>
              </Box>
            </Fade>

            {/* NOMINAL → Permitir transferência */}
            <Fade in={!!ticketIsNominal} mountOnEnter unmountOnExit>
              <Box>
                <FormControlLabel
                  control={
                    <SwitchRound
                      disabled={isAdmin}
                      size="small"
                      checked={!!ticketAllowTransfer}
                      onChange={(e) =>
                        setValue('ticketAllowTransfer', e.target.checked, {
                          shouldDirty: true,
                        })
                      }
                      sx={{ m: 1 }}
                    />
                  }
                  label={
                    <Typography variant="body1" sx={{ color: '#707275' }}>
                      Permitir transferência de ingressos
                    </Typography>
                  }
                />
              </Box>
            </Fade>
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems="center"
          >
            <BasicDateTimePicker
              label="Data e hora do evento"
              value={watch('eventDate')}
              setValue={(date) =>
                setValue('eventDate', date, {
                  shouldDirty: true,
                })
              }
            />
            <Box width="100%">
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Quantidade máxima disponível para venda"
                {...register('maximumQuantity', { required: true })}
                inputProps={{
                  min: 0,
                }}
                InputLabelProps={{
                  shrink: (watch('maximumQuantity') || '') !== '',
                }}
                error={
                  errors.maximumQuantity?.type === 'required' ||
                  errors.maximumQuantity?.type === 'invalidNumber'
                }
                onKeyDown={(e) => {
                  if (e.key === '-' || e.key === 'e' || e.key === '+') {
                    e.preventDefault()
                  }
                }}
              />
              {errors.maximumQuantity && (
                <Typography variant="caption" color={'red'}>
                  {errors.maximumQuantity?.message ||
                    "Para o modelo 'Ingresso' é necessário informar a quantidade máxima disponível para venda"}
                </Typography>
              )}
            </Box>
          </Stack>

          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '20px',
              color: '#38393B',
            }}
          >
            Local do evento
          </Typography>

          <Controller
            name="eventLocationName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label="Nome do local"
                fullWidth
              />
            )}
          />

          <Controller
            name="eventAddress"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label="Endereço do evento"
                fullWidth
              />
            )}
          />

          <Controller
            name="eventLocationUrl"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label="Link do local (Google Maps, Waze, etc.)"
                fullWidth
              />
            )}
          />

          {!isAdmin && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="end"
            >
              {buttonSave && (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{ textTransform: 'none' }}
                >
                  Salvar
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </>
  )
}

export default ProductTicketSettings
